import { useMemo } from 'react';
import { Empty } from 'antd';
import { groupSnapshotData } from '@/shared/history';

/**
 * SnapshotFields
 *
 * Renders a captured record as `field / value` rows grouped into sections,
 * replacing the JSON dump the drawer used to show.
 *
 * Populated references collapse to their name: an employee snapshot embeds the
 * whole company, sector and author documents, which is about 300 of its 438
 * fields and buries the employee's own ~140.
 */
export default function SnapshotFields({ data, modelName, t, locale }) {
  const grouped = useMemo(
    () => groupSnapshotData(data, modelName, { t, locale }),
    [data, modelName, t, locale]
  );

  if (!grouped.totalFields) {
    return <Empty description={t('ui.noSnapshots')} className="my-4" />;
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500">
        {t('ui.fieldsInSnapshot', { count: grouped.totalFields })}
      </div>

      {grouped.sections.map((section) => (
        <div key={section.key}>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {section.label}
          </div>
          <table className="w-full table-fixed border-collapse text-sm">
            <tbody>
              {section.rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 last:border-0"
                >
                  <th
                    scope="row"
                    className="w-1/2 py-1 pr-3 text-left align-top font-medium text-gray-600"
                    title={row.known ? undefined : row.path}
                  >
                    {row.label}
                  </th>
                  <td className="w-1/2 break-words py-1 align-top text-gray-900">
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
